import React, { useMemo } from "react";
import {
  CheckCircle2,
  CircleDashed,
  MapPin,
  Users,
  UserPlus,
  Package,
  Route,
} from "lucide-react";

type StepStatus = "todo" | "progress" | "ok";

export type OrganisationTerrainWorkflowProps = {
  zoneCount: number;
  equipeCount: number;
  agentsAffectes: number;
  agentsSansEquipe: number;
  ressourcesAffectees: number;
  tourneesJour: number;
  onCreateZone: () => void;
  onCreateEquipe: () => void;
  onAffecterAgents: () => void;
  onAffecterRessources: () => void;
  onGenererTournees: () => void;
};

type StepRow = {
  key: string;
  title: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  st: StepStatus;
  tone: string;
  action: () => void;
  actionLabel: string;
  counter?: number;
};

function WorkflowStepCard({
  step,
  indexDisplay,
}: {
  step: StepRow;
  indexDisplay: number;
}): React.JSX.Element {
  const Icon = step.icon;
  const IconStatus = step.st === "ok" ? CheckCircle2 : CircleDashed;
  return (
    <li className="flex h-full min-h-[8.5rem] flex-col gap-3 rounded-xl border border-slate-800/80 bg-slate-900/40 px-3 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-xs font-bold text-slate-400">
          {indexDisplay}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="font-medium leading-snug text-slate-200">{step.title}</span>
          </div>
          <span className={`mt-1 inline-flex items-center gap-1 text-xs ${step.tone}`}>
            <IconStatus className="h-3.5 w-3.5 shrink-0" />
            {step.label}
          </span>
          <p className="mt-1 text-xs text-slate-500">{step.desc}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={step.action}
        className="mt-auto w-full rounded-lg bg-emerald-600/90 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 sm:w-auto sm:self-start"
      >
        {step.actionLabel}
      </button>
    </li>
  );
}

function statusOf(
  done: boolean,
  partial: boolean
): { label: string; st: StepStatus; tone: string } {
  if (done) {
    return { label: "OK", st: "ok", tone: "text-emerald-400" };
  }
  if (partial) {
    return { label: "En cours", st: "progress", tone: "text-amber-400" };
  }
  return { label: "À faire", st: "todo", tone: "text-slate-500" };
}

const OrganisationTerrainWorkflow: React.FC<OrganisationTerrainWorkflowProps> = ({
  zoneCount,
  equipeCount,
  agentsAffectes,
  agentsSansEquipe,
  ressourcesAffectees,
  tourneesJour,
  onCreateZone,
  onCreateEquipe,
  onAffecterAgents,
  onAffecterRessources,
  onGenererTournees,
}) => {
  const hint = useMemo(() => {
    if (zoneCount >= 1 && equipeCount === 0) {
      return `${zoneCount} zone${zoneCount > 1 ? "s" : ""} détectée${zoneCount > 1 ? "s" : ""}. Créez une équipe pour exploiter cette zone.`;
    }
    if (equipeCount >= 1 && agentsAffectes === 0 && agentsSansEquipe > 0) {
      return "Des agents sont disponibles sans équipe — affectez-les pour rendre les équipes opérationnelles.";
    }
    if (equipeCount >= 1 && ressourcesAffectees === 0) {
      return "Affectez du matériel ou des équipements aux équipes pour sécuriser les tournées.";
    }
    return null;
  }, [zoneCount, equipeCount, agentsAffectes, agentsSansEquipe, ressourcesAffectees]);

  const steps = useMemo(() => {
    const s1 = statusOf(zoneCount > 0, false);
    const s2 = statusOf(
      equipeCount > 0,
      zoneCount > 0 && equipeCount === 0
    );
    const s3 = statusOf(
      agentsAffectes > 0,
      equipeCount > 0 && agentsAffectes === 0
    );
    const s4 = statusOf(
      ressourcesAffectees > 0,
      equipeCount > 0 && ressourcesAffectees === 0
    );
    const s5 = statusOf(tourneesJour > 0, equipeCount > 0 && tourneesJour === 0);

    return [
      {
        key: "z",
        title: "Créer des zones",
        desc: `${zoneCount} zone${zoneCount === 1 ? "" : "s"}`,
        icon: MapPin,
        ...s1,
        action: onCreateZone,
        actionLabel: "+ Zone",
        counter: zoneCount,
      },
      {
        key: "e",
        title: "Créer des équipes",
        desc: `${equipeCount} équipe${equipeCount === 1 ? "" : "s"}`,
        icon: Users,
        ...s2,
        action: onCreateEquipe,
        actionLabel: "+ Équipe",
        counter: equipeCount,
      },
      {
        key: "a",
        title: "Affecter les agents",
        desc: `${agentsSansEquipe} sans équipe`,
        icon: UserPlus,
        ...s3,
        action: onAffecterAgents,
        actionLabel: "Affecter",
        counter: agentsAffectes,
      },
      {
        key: "r",
        title: "Affecter les ressources",
        desc: `${ressourcesAffectees} affectée${ressourcesAffectees === 1 ? "" : "s"}`,
        icon: Package,
        ...s4,
        action: onAffecterRessources,
        actionLabel: "Ressources",
        counter: ressourcesAffectees,
      },
      {
        key: "t",
        title: "Générer les tournées",
        desc: `${tourneesJour} aujourd’hui`,
        icon: Route,
        ...s5,
        action: onGenererTournees,
        actionLabel: "Tournées",
        counter: tourneesJour,
      },
    ];
  }, [
    zoneCount,
    equipeCount,
    agentsAffectes,
    agentsSansEquipe,
    ressourcesAffectees,
    tourneesJour,
    onCreateZone,
    onCreateEquipe,
    onAffecterAgents,
    onAffecterRessources,
    onGenererTournees,
  ]);

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5 ring-1 ring-white/5">
      <h3 className="text-sm font-semibold text-slate-200">Workflow guidé</h3>
      <p className="mt-1 text-xs text-slate-500">
        Zone → Équipe → Agents → Ressources → Tournée / passages
      </p>
      {hint ? (
        <p className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
          {hint}
        </p>
      ) : null}

      {/* Une grille 3 colonnes : lignes 1–3 puis 4–5 dans les colonnes 1–2 (même largeur de carte que le haut) */}
      <ol className="m-0 mt-4 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3 md:gap-4 md:items-stretch">
        {steps.map((step, i) => (
          <WorkflowStepCard key={step.key} step={step} indexDisplay={i + 1} />
        ))}
      </ol>
    </div>
  );
};

export default OrganisationTerrainWorkflow;
